import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Table,
  Text,
} from "@chakra-ui/react";
import { Info, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";

import GenericTable from "@/components/GenericTable";
import { ROUTES } from "@/config/routes";
import { formatDatetime } from "@/helpers/time";
import CommonLayout from "../CommonLayout";
import { OrgQueryError } from "./components/OrgQueryError";
import {
  REQUEST_STATUS_COLORS,
  REQUEST_STATUS_LABELS,
} from "./merchantRequest/constants";
import { useOrgMerchantRequests } from "./hooks/useOrgMerchantRequests";

const MERCHANT_COLUMNS = [
  { key: "displayName", label: "Comercio", minWidth: 260 },
  { key: "active", label: "Estado", minWidth: 120 },
] as const;

const REQUEST_COLUMNS = [
  { key: "displayName", label: "Comercio solicitado", minWidth: 260 },
  { key: "createdAt", label: "Creada", minWidth: 160 },
  { key: "requestedBy", label: "Solicitada por", minWidth: 220 },
  { key: "status", label: "Estado", minWidth: 140 },
  { key: "actions", label: "", minWidth: 120 },
] as const;

const SectionHeader = ({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: JSX.Element;
}): JSX.Element => (
  <Flex
    mb={3}
    gap={4}
    justifyContent="space-between"
    alignItems={{ base: "stretch", md: "center" }}
    flexDirection={{ base: "column", md: "row" }}
  >
    <Box>
      <Text fontSize="md" fontWeight="semibold" color="gray.900">
        {title}
      </Text>
      <Text fontSize="sm" color="gray.500">
        {description}
      </Text>
    </Box>
    {action}
  </Flex>
);

const OrgMerchantsContainer = (): JSX.Element => {
  const navigate = useNavigate();
  const { merchants, requests, isLoading, isError, errorCode, refetch } =
    useOrgMerchantRequests();

  if (isError)
    return (
      <CommonLayout
        title="Comercios"
        table={
          <OrgQueryError
            message="los comercios de la organización"
            code={errorCode}
            onRetry={refetch}
          />
        }
      />
    );

  const table = (
    <Box display="flex" flexDirection="column" gap="2rem">
      <Box>
        <SectionHeader
          title="Comercios de la organización"
          description="Los edificios y conjuntos que ya recaudan con Palomma bajo esta organización."
        />
        <GenericTable isLoading={isLoading} columns={MERCHANT_COLUMNS}>
          {merchants.map((merchant) => (
            <Table.Row key={merchant.id}>
              <Table.Cell color="gray.700">{merchant.displayName}</Table.Cell>
              <Table.Cell>
                <Badge
                  colorPalette={merchant.active ? "green" : "red"}
                  variant="subtle"
                >
                  {merchant.active ? "Activo" : "Inactivo"}
                </Badge>
              </Table.Cell>
            </Table.Row>
          ))}
        </GenericTable>
      </Box>

      <Box>
        <SectionHeader
          title="Solicitudes"
          description="Cada solicitud entra a la cola de revisión de Palomma. Cuando se aprueba, el comercio aparece arriba."
          action={
            <Button
              colorPalette="palomma"
              onClick={() => navigate(ROUTES.DASHBOARD.MERCHANT_REQUEST)}
            >
              <Plus size={18} />
              Agregar comercio
            </Button>
          }
        />
        <GenericTable isLoading={isLoading} columns={REQUEST_COLUMNS}>
          {requests.map((request) => (
            <Table.Row key={request.id}>
              <Table.Cell color="gray.700">
                <Box>
                  <Text>{request.displayName ?? "Sin nombre"}</Text>
                  {/* The returned message is the whole point of the return
                      flow, and invisible if the row only carries a badge. */}
                  {request.customerFacingSummary &&
                    request.status === "draft" && (
                      <HStack
                        gap={2}
                        alignItems="flex-start"
                        mt={1}
                        maxW="30rem"
                      >
                        <Box flexShrink={0} mt={0.5}>
                          <Info
                            size={14}
                            color="var(--chakra-colors-orange-500)"
                          />
                        </Box>
                        <Text
                          fontSize="xs"
                          color="orange.700"
                          lineHeight="tall"
                          whiteSpace="normal"
                        >
                          {request.customerFacingSummary}
                        </Text>
                      </HStack>
                    )}
                </Box>
              </Table.Cell>
              <Table.Cell color="gray.500">
                {formatDatetime(request.createdAt)}
              </Table.Cell>
              <Table.Cell color="gray.500">
                {request.requestedBy ?? "—"}
              </Table.Cell>
              <Table.Cell>
                <Badge
                  colorPalette={REQUEST_STATUS_COLORS[request.status]}
                  variant="subtle"
                >
                  {REQUEST_STATUS_LABELS[request.status]}
                </Badge>
              </Table.Cell>
              <Table.Cell py={0}>
                {request.status === "draft" && (
                  <Button
                    size="xs"
                    h="26px"
                    px={3}
                    colorPalette="palomma"
                    variant="outline"
                    fontWeight="medium"
                    onClick={() =>
                      navigate(
                        ROUTES.DASHBOARD.MERCHANT_REQUEST_DETAIL.replace(
                          ":id",
                          request.id
                        )
                      )
                    }
                  >
                    Continuar
                  </Button>
                )}
              </Table.Cell>
            </Table.Row>
          ))}
        </GenericTable>
      </Box>
    </Box>
  );

  return (
    <CommonLayout
      title="Comercios"
      subtitle="Los comercios de tu organización y las solicitudes en curso"
      table={table}
    />
  );
};

export default OrgMerchantsContainer;
